import React, { useCallback, useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { LoadingOverlay } from 'dashboard/components/common';
import CustomerLoginToggle from './CustomerLoginToggle';
import SignInProviders from './SignInProviders';
import AnnouncementBar from './AnnouncementBar';
import Favicon from './Favicon';
import AiToggle from './AiToggle';

/**
 * The settings that apply to the whole site rather than to one page.
 *
 * All five of these lived on Brand settings, beside the company's address and
 * its bank details, which is where you look for who the business is — not for
 * what the site does. They belong here: the announcement bar sits above the
 * header, the sign-in link sits inside it, the tab icon is the site's own
 * mark, and "Build with AI" writes the pages. Global design is the screen
 * that already owns the header, the footer and the not-found page.
 *
 * Ordered by how much of the site each one touches: who can get in, what
 * greets them, what the browser tab shows, and finally the tool that writes
 * the pages.
 */
export default function SiteTab({ brandId }) {
  const [brandData, setBrandData] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(() => {
    if (!brandId) { setLoading(false); return Promise.resolve(); }
    setLoading(true);
    return API.getBrandById({ brand_id: brandId })
      .then((res) => setBrandData(res || {}))
      .catch((err) => toast.error(err?.message || 'Could not load the brand'))
      .finally(() => setLoading(false));
  }, [brandId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <LoadingOverlay loading={loading} label='Loading site settings…' minHeight={320}>
      <Stack direction='column' spacing='24px'>
        <CustomerLoginToggle brandData={brandData} fetchData={fetchData} />
        {/* Only worth setting up once customers can sign in at all. */}
        {brandData?.customer_login_enabled && <SignInProviders brandId={brandId} />}
        <AnnouncementBar brandData={brandData} fetchData={fetchData} />
        <Favicon brandData={brandData} fetchData={fetchData} />
        <AiToggle brandData={brandData} fetchData={fetchData} />
      </Stack>
    </LoadingOverlay>
  );
}
